import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { createCheckoutUrl, formatPrice } from "@/lib/shopify";

const STORAGE_KEY = "bb_cart_v1";
const CartContext = createContext(null);

export function useCart() {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used inside <CartProvider>");
    return ctx;
}

export function CartProvider({ children }) {
    const [items, setItems] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });
    const [open, setOpen] = useState(false);
    const [checkingOut, setCheckingOut] = useState(false);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
        } catch {
            /* ignore storage errors */
        }
    }, [items]);

    const addItem = useCallback((product, quantity = 1) => {
        if (!product?.variantId) return;
        setItems((prev) => {
            const idx = prev.findIndex((i) => i.variantId === product.variantId);
            if (idx >= 0) {
                const next = [...prev];
                next[idx] = { ...next[idx], quantity: next[idx].quantity + quantity };
                return next;
            }
            return [
                ...prev,
                {
                    variantId: product.variantId,
                    handle: product.handle,
                    title: product.title,
                    image: product.image?.url || null,
                    price: product.price,
                    quantity,
                },
            ];
        });
        setOpen(true);
    }, []);

    const updateQuantity = useCallback((variantId, quantity) => {
        setItems((prev) =>
            prev
                .map((i) =>
                    i.variantId === variantId ? { ...i, quantity: Math.max(0, quantity) } : i
                )
                .filter((i) => i.quantity > 0)
        );
    }, []);

    const removeItem = useCallback((variantId) => {
        setItems((prev) => prev.filter((i) => i.variantId !== variantId));
    }, []);

    const clear = useCallback(() => setItems([]), []);

    const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);
    const subtotal = items.reduce(
        (sum, i) => sum + (i.price?.amount || 0) * i.quantity,
        0
    );
    const currency = items[0]?.price?.currency || "USD";

    const checkout = useCallback(async () => {
        if (!items.length) return;
        setCheckingOut(true);
        try {
            // Build lines for cartCreate
            const lines = items.map((i) => ({ merchandiseId: i.variantId, quantity: i.quantity }));
            // Use the same createCheckoutUrl helper but with multiple lines
            const url = await createMultiLineCheckout(lines);
            window.location.href = url;
        } catch (err) {
            setCheckingOut(false);
            throw err;
        }
    }, [items]);

    const value = {
        items,
        itemCount,
        subtotal,
        currency,
        open,
        setOpen,
        addItem,
        updateQuantity,
        removeItem,
        clear,
        checkout,
        checkingOut,
        formatTotal: () => formatPrice({ amount: subtotal, currency }),
    };

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

// Local helper: multi-line cart create that returns checkoutUrl
async function createMultiLineCheckout(lines) {
    if (lines.length === 1) {
        return createCheckoutUrl(lines[0].merchandiseId, lines[0].quantity);
    }
    // Manual fetch for multi-line cart
    const DOMAIN = process.env.REACT_APP_SHOPIFY_DOMAIN;
    const TOKEN = process.env.REACT_APP_SHOPIFY_STOREFRONT_TOKEN;
    const API_VERSION = process.env.REACT_APP_SHOPIFY_API_VERSION || "2024-10";
    const ENDPOINT = `https://${DOMAIN}/api/${API_VERSION}/graphql.json`;
    const query = `
        mutation CartCreate($lines: [CartLineInput!]!) {
            cartCreate(input: { lines: $lines }) {
                cart { checkoutUrl }
                userErrors { message }
            }
        }
    `;
    const res = await fetch(ENDPOINT, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": TOKEN,
        },
        body: JSON.stringify({ query, variables: { lines } }),
    });
    const json = await res.json();
    const err = json.data?.cartCreate?.userErrors?.[0]?.message;
    if (err) throw new Error(err);
    const url = json.data?.cartCreate?.cart?.checkoutUrl;
    if (!url) throw new Error("Could not create checkout");
    return url;
}
